import Vue from 'vue';
import { modernAccountRepository } from '@/shells/api/modernAccountRepository';

const ZENDESK_SCRIPT_ID = 'ze-snippet';
const ZENDESK_KEY = 'e469a181-7e42-4a98-ac10-6d3bc1471048';

export function rolesAllowModernChat(roles = {}) {
  return Object.values(roles || {}).some(role => String(role).toLowerCase().includes('chat'));
}

export class ModernChatService {
  constructor(repository = modernAccountRepository, windowRef = window, documentRef = document) {
    this.repository = repository;
    this.window = windowRef;
    this.document = documentRef;
    this.loadPromise = null;
    this.state = Vue.observable({ available: false, status: 'idle', error: null });
  }

  async checkAvailability(roles) {
    if (!rolesAllowModernChat(roles)) {
      this.state.available = false;
      return false;
    }
    try {
      const setting = await this.repository.getChatDisplaySetting();
      // 0 used to mean floating-only; the launcher now always lives in the menu.
      this.state.available = setting === 0 || setting === 1 || setting === 2;
    } catch (error) {
      this.state.available = false;
    }
    return this.state.available;
  }

  load() {
    if (typeof this.window.zE === 'function') {
      this.configureWidget();
      this.state.status = 'ready';
      this.state.error = null;
      return Promise.resolve();
    }
    if (this.loadPromise) return this.loadPromise;

    this.state.status = 'loading';
    this.loadPromise = new Promise((resolve, reject) => {
      let existing = this.document.getElementById(ZENDESK_SCRIPT_ID);
      if (existing && existing.__eurekersFailed) { existing.remove(); existing = null; }
      const script = existing || this.document.createElement('script');
      let settled = false, poll;
      const cleanup = () => { this.window.clearTimeout(timeout); this.window.clearTimeout(poll); };
      const fail = () => {
        if (settled) return;
        settled = true; cleanup(); script.__eurekersFailed = true;
        reject(new Error('No se pudo cargar el chat. Puedes reintentarlo.'));
      };
      const timeout = this.window.setTimeout(fail, 12000);
      const finish = () => {
        if (settled) return;
        this.window.clearTimeout(poll);
        if (typeof this.window.zE !== 'function') { poll = this.window.setTimeout(finish, 100); return; }
        settled = true; cleanup(); resolve();
      };
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', fail, { once: true });
      if (!existing) {
        script.id = ZENDESK_SCRIPT_ID;
        script.async = true;
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`;
        this.document.head.appendChild(script);
      }
      finish();
    }).then(() => {
      this.configureWidget();
      this.state.status = 'ready';
      this.state.error = null;
    }).catch(error => {
      this.state.status = 'error';
      this.state.error = error;
      this.loadPromise = null;
      throw error;
    });

    return this.loadPromise;
  }

  configureWidget() {
    if (this.configuredZE === this.window.zE) return;
    this.window.zE('webWidget', 'hide');
    this.window.zE('webWidget:on', 'close', () => this.window.zE('webWidget', 'hide'));
    this.configuredZE = this.window.zE;
  }

  async open() {
    await this.load();
    // Open in-page: popout can be blocked silently, especially after async load.
    this.window.zE('webWidget', 'show');
    this.window.zE('webWidget', 'open');
  }
}

export const modernChatService = new ModernChatService();
